import React, { useEffect, useState } from 'react';
import { Page } from '../../typings/reviews';
import { useFeatureServices } from '../../services/use-feature-services';
import { ReviewsContainer } from './styled';
import { Link, LoaderSpinner, Spinner } from '@vw-marketing/us-components';
import NoReviews from '../no-reviews';
import { FeatureAppTrackingManager } from '../../services/use-tracking-manager';
import { useFeatureAppConfig } from '../../services/use-feature-app-config';
import { DealerComment, DealerReview } from '../../typings/dealer-review';
import { useRecoilState, useRecoilValue } from 'recoil';
import { dataVersion } from '../dealer-reviews-loader';
import { fetchDealerReviews } from '../../fetch-data/fetch-dealer-reviews';
import DealerReviewComment from '../dealer-review-comment';
import { filters } from '.';

interface ReviewsHandlerProps {
  pageNumber: number;
  readonly dealerReviews: DealerReview;
  readonly pageData?: Page;
  readonly reverse?: boolean;
  readonly rating?: string;
  readonly trackingManager?: FeatureAppTrackingManager;
  handleResetFilters: () => void;
  onHandleMore: () => void;
}
const DealerReviewsHandler: React.FC<ReviewsHandlerProps> = props => {
  const sliceOffset = 5;
  const initialOffset = 10;
  const [appFilters, setAppFilters] = useRecoilState(filters);
  const { apiKeyContent } = useFeatureAppConfig();
  const { 'service-config-provider': serviceProvider } = useFeatureServices();
  const reviewsHostData = serviceProvider.configs['fiveStars'];
  const dv = useRecoilValue(dataVersion);
  const { handleResetFilters, dealerReviews, onHandleMore } = props;
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [reviews, setReviews] = useState<DealerComment[]>([]);
  const [nextReviews, setNextReviews] = useState<DealerComment[]>([]);
  const requestNewReviews = async (
    pageNumber: number,
    rating?: string,
    type?: string,
    resetData?: boolean,
  ) => {
    let hasToRequest = !resetData ? nextReviews.length >= sliceOffset : true;
    let reviewsResponse: DealerComment[]= [];
    let displayedReviews: DealerComment[] = [];
    let nextReviewsToDisplay: DealerComment[] = [];
    let nextPageNumber = 3;

    // Loading data for no blocking user while requesting
    if (!resetData) {
      displayedReviews = [...reviews, ...nextReviews];
      setReviews(displayedReviews);
    }

    if (hasToRequest) {
      reviewsResponse = await fetchDealerReviews({
        reviewsHostData,
        dealerId: dealerReviews.dealer_id,
        dataVersion: dv,
        apiKeyContent,
        page: pageNumber,
        size: resetData ? initialOffset : sliceOffset,
        rating,
        type,
      });
    }
    

    if (!resetData) {
      nextReviewsToDisplay = reviewsResponse;
      nextPageNumber = pageNumber + 1;
    } else {
      displayedReviews = reviewsResponse.slice(0, sliceOffset);
      nextReviewsToDisplay = reviewsResponse.slice(sliceOffset);
      setReviews(displayedReviews);
    }
    setNextReviews(nextReviewsToDisplay);
    setAppFilters({
      ...appFilters,
      reviewsOffset: nextPageNumber,
      hasMoreReviews: nextReviewsToDisplay.length > 0,
      disableLoading: false,
      isResetFilter: false,
      isLoadingMore: false,
      wasSearch: true,
    });
  };

  useEffect(() => {
    if (!appFilters.wasSearch) {
      !appFilters.disableLoading && setIsLoadingReviews(true);
      requestNewReviews(
        appFilters.reviewsOffset,
        appFilters.rating?.value,
        appFilters.type?.value,
        appFilters.isResetFilter,
      )
        .then(() => {})
        .catch(() => {
          !appFilters.disableLoading && setReviews([]);
          setAppFilters({
            ...appFilters,
            wasSearch: true,
            isLoadingMore: false,
            disableLoading: false,
            hasMoreReviews: false,
          });
        })
        .finally(() => {
          setIsLoadingReviews(false);
        });
    }
  }, [appFilters]);

  return (
    <>
      {!isLoadingReviews ? (
        <>
          {reviews?.length ? (
            <>
              <ReviewsContainer>
                {reviews.map((review: DealerComment) => (
                  <DealerReviewComment
                    review={review}
                    key={review.comment_id}
                    onHandleChangeTracking={() => {}}
                  />
                ))}
                {appFilters.hasMoreReviews && (
                  <div className="view-more-cta" tabIndex={0}>
                    {appFilters.isLoadingMore ? (
                      <Spinner />
                    ) : (
                      <Link onClick={onHandleMore} isInline>
                        View More
                      </Link>
                    )}
                  </div>
                )}
              </ReviewsContainer>
            </>
          ) : (
            <NoReviews onReset={handleResetFilters} />
          )}
        </>
      ) : (
        <LoaderSpinner />
      )}
    </>
  );
};

export default DealerReviewsHandler;
